export enum Endpoints {
  /** The signed-in internal user's identity, role and permissions — the admin's access gate. */
  InternalAuthMe = '/v1/internal/auth/me',

  // Internal admin accounts and roles (ENG-806). Under `/access/` on purpose:
  // `/internal/partners/:partnerId/users` already means partner dashboard users.
  AdminFetchInternalUsers = '/v1/internal/access/users',
  AdminInviteInternalUser = '/v1/internal/access/users',
  AdminAssignInternalUserRole = '/v1/internal/access/users/:userId/role',
  AdminSetInternalUserStatus = '/v1/internal/access/users/:userId/status',
  AdminFetchInternalRoles = '/v1/internal/access/roles',
  AdminCreateInternalRole = '/v1/internal/access/roles',
  AdminFetchInternalRole = '/v1/internal/access/roles/:roleId',
  AdminUpdateInternalRole = '/v1/internal/access/roles/:roleId',

  /**
   * POST, no body. Returns `{ accessToken, refreshToken }` minted for a shared GigSafe super_admin
   * account, scoped to the customer; used to deep-link into the dashboard's /sign-in-as (see
   * buildSignInAsUrl).
   */
  AdminLoginAsCustomer = '/v1/internal/partners/:customerId/login-as',

  // The frontend says "customer" where the API says "partner"; that vocabulary split predates
  // the internal API and is left alone.
  AdminFetchCustomers = '/v1/internal/partners',
  AdminCreateCustomer = '/v1/internal/partners',
  AdminFetchCustomer = '/v1/internal/partners/:customerId',
  // PUT — same PartnerUpdateDTO the Retool app used; `logo` is a data URI, see fileToDataUrl.
  AdminUpdateCustomer = '/v1/internal/partners/:customerId',
  // The path segment is `buyer`; the row behind it is the customer's payment configuration. GET
  // and PUT share one path and stay two members so each call site names its intent.
  AdminFetchCustomerPaymentConfig = '/v1/internal/partners/:customerId/buyer',
  AdminUpdateCustomerPaymentConfig = '/v1/internal/partners/:customerId/buyer',
  AdminOnboardMasterContractor = '/v1/internal/partners/onboard-master-contractor',

  // The doubled `gigsafe-config/gigsafe-config` segment the Retool route carried is gone: the
  // internal router mounts the controller once, under /partners.
  AdminFetchGigsafeConfig = '/v1/internal/partners/gigsafe-config',
  AdminCreateGigsafeConfig = '/v1/internal/partners/gigsafe-config',
  AdminUpdateGigsafeConfig = '/v1/internal/partners/gigsafe-config',

  // Invoices moved out of /funding/:partnerId and under the partner itself. Every action route
  // takes a PARTNER invoice id — there is no composed-invoice endpoint, which is why a
  // composed-level action is a loop in `sagas/invoice.ts`.
  AdminFetchInvoices = '/v1/internal/partners/:partnerId/invoices',
  AdminFetchInvoiceDueInterest = '/v1/internal/partners/:partnerId/invoices/due-interest',
  // Declared alongside the Invoice-tab actions, but this route belongs to the Business tab —
  // Notion Feature 10.4, deferred to ENG-482. See the 2026-09-09-admin-customer-business design.
  AdminUploadHistoricalInvoices = '/v1/internal/partners/:partnerId/invoices/upload-historical-invoices',
  AdminInvoiceCancel = '/v1/internal/partners/:partnerId/invoices/:invoiceId/cancel',
  AdminInvoiceMarkFunded = '/v1/internal/partners/:partnerId/invoices/:invoiceId/funded',
  AdminInvoiceMarkFundedNoCheck = '/v1/internal/partners/:partnerId/invoices/:invoiceId/funded-no-check',
  AdminInvoiceProcessPayment = '/v1/internal/partners/:partnerId/invoices/:invoiceId/process-payment',
  AdminInvoiceRepayment = '/v1/internal/partners/:partnerId/invoices/:invoiceId/repayment',
  AdminInvoiceFeesRepayment = '/v1/internal/partners/:partnerId/invoices/:invoiceId/fees-repayment',
  AdminInvoiceRepayDueInterest = '/v1/internal/partners/:partnerId/invoices/:invoiceId/repay-due-interest',
  // POST places or REPLACES the hold (that is how rescheduling works); DELETE cancels it.
  AdminInvoicePaymentReleaseSchedule = '/v1/internal/partners/:partnerId/invoices/:invoiceId/payment-release-schedule',

  // The customer's own staff logins, the invites that create them and the roles those invites
  // assign. These were CustomerAuthGuard routes under /v1/gigsafe that read the partner off a
  // login-as token; on the internal API the partner is a PATH PARAM and the admin's own token
  // carries the customer_users_* / customer_roles_* / customer_invites_* permissions.
  AdminFetchGigsafeUsers = '/v1/internal/partners/:partnerId/users',
  AdminFetchGigsafeRoles = '/v1/internal/partners/:partnerId/roles',
  AdminCreateGigsafeRole = '/v1/internal/partners/:partnerId/roles',
  // POST, not PUT — the update route is declared as @Post('/:roleId').
  AdminUpdateGigsafeRole = '/v1/internal/partners/:partnerId/roles/:roleId',
  AdminFetchGigsafeRolePermissions = '/v1/internal/partners/:partnerId/roles/permissions/all',
  AdminCreateGigsafeInvite = '/v1/internal/partners/:partnerId/invites',

  AdminFetchPartnerProducts = '/v1/internal/partner-products',
  AdminCreatePartnerProduct = '/v1/internal/partner-products/:partnerId',
  AdminUpdatePartnerProduct = '/v1/internal/partner-products/:partnerId',
  // Note DELETE takes a request BODY, not params: `{ product, productId }`.
  AdminDeactivatePartnerProduct = '/v1/internal/partner-products/:partnerId',

  // Deliberately "…TemplateList", not "…Templates": `AdminFetchOnboardingTemplate` is the
  // editor's single-template GET and needs a `:templateId` param substitution. Two members
  // differing by one `s` would be a wrong-endpoint bug waiting to happen.
  AdminFetchOnboardingTemplateList = '/v1/internal/onboarding/collect-data-templates',
  // Same path as the list endpoint — para-node#6811 swapped this POST in place to the actions-based
  // create DTO, so there is no new URL and no version bump on the internal API.
  AdminCreateOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates',
  AdminFetchOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates/:templateId',
  AdminSaveOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates/:templateId',
  AdminPreviewOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates/:collectDataTemplateId/preview',
  /**
   * The `/name` suffix is load-bearing. Without it this lands on the template UPDATE route,
   * which takes a whole template body — a rename would then overwrite the template rather than
   * rename it. The only route in this enum where a wrong path corrupts data instead of 404ing.
   */
  AdminRenameOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates/:collectDataTemplateId/name',
  // Same path as `AdminSaveOnboardingTemplate`, but its own member for the `DELETE` verb and the
  // `:collectDataTemplateId` param name the delete saga substitutes.
  AdminDeleteOnboardingTemplate = '/v1/internal/onboarding/collect-data-templates/:collectDataTemplateId',

  // GET and POST both live on `AdminDocusignPartnerTemplates` — same path, different verb.
  // Only the DELETE needs its own member, for the `:templateId` substitution.
  AdminDocusignPartnerTemplates = '/v1/internal/onboarding/docusign-templates',
  AdminDeleteDocusignTemplate = '/v1/internal/onboarding/docusign-templates/:templateId',

  // Three Retool paths (partner-markets, market, ops-tools/market/:id) collapse onto one resource.
  AdminFetchMarkets = '/v1/internal/onboarding/markets',
  AdminCreateMarket = '/v1/internal/onboarding/markets',
  AdminRenameMarket = '/v1/internal/onboarding/markets/:marketId',

  AdminLmsTrainingList = '/v1/internal/onboarding/lms-trainings',
  AdminCreateLmsTraining = '/v1/internal/onboarding/lms-trainings',
  AdminUpdateLmsTraining = '/v1/internal/onboarding/lms-trainings/:lmsTrainingId',
  AdminDeleteLmsTraining = '/v1/internal/onboarding/lms-trainings/:lmsTrainingId',

  // Remote config (ENG-1309). GET and POST share the collection path; PATCH and DELETE take the id.
  AdminRemoteConfigs = '/v1/internal/remote-config',
  AdminRemoteConfig = '/v1/internal/remote-config/:remoteConfigId',

  AdminFetchInsuranceProducts = '/v1/internal/insurance/products',
  AdminCreateInsuranceProduct = '/v1/internal/insurance/products',
  AdminUpdateInsuranceProduct = '/v1/internal/insurance/products/:insuranceProductId',
  AdminDeleteInsuranceProduct = '/v1/internal/insurance/products/:insuranceProductId',

  // Every policy route addresses the PERMANENT id, never the version-specific `id`.
  AdminInsurancePolicies = '/v1/internal/insurance/policies',
  AdminInsurancePolicy = '/v1/internal/insurance/policies/:insurancePolicyPermanentId',
  AdminInsurancePolicyProducts = '/v1/internal/insurance/policies/:insurancePolicyPermanentId/products',
  AdminInsurancePolicyProduct = '/v1/internal/insurance/policies/:insurancePolicyPermanentId/products/:insuranceProductId',

  // The GET answers 404 for a partner with no business row — an expected empty state, not a
  // failure. See docs/superpowers/specs/2026-09-09-admin-customer-business-design.md §2.
  AdminFetchOatfiBusiness = '/v1/internal/integrations/oatfi/:partnerId/business',
  AdminCreateOatfiBusiness = '/v1/internal/integrations/oatfi/:partnerId/business',
  AdminUpdateOatfiBusiness = '/v1/internal/integrations/oatfi/:partnerId/business',
  AdminRequestOatfiUnderwriting = '/v1/internal/integrations/oatfi/:partnerId/business/underwriting',
  AdminUploadOatfiSupplementalData = '/v1/internal/integrations/oatfi/:partnerId/business/supplemental',
  AdminCreateOatfiPaymentMethod = '/v1/internal/integrations/oatfi/:partnerId/business/payment-methods',
  AdminFetchPlaidLinkToken = '/v1/internal/integrations/plaid/:partnerId/link-token',
  // Left `dashboard/` for `integrations/` — the only yardstik route the admin calls.
  AdminYardstikPackages = '/v1/internal/integrations/yardstik/packages',

  // GET/POST/PUT share one path. They stay three members so each call site names its intent
  // and a wrong-verb bug is visible where it is written.
  AdminFetchOAuthClient = '/v1/internal/oauth/:partnerId/client',
  AdminCreateOAuthClient = '/v1/internal/oauth/:partnerId/client',
  AdminUpdateOAuthClient = '/v1/internal/oauth/:partnerId/client',
  AdminRevokeOAuthTokens = '/v1/internal/oauth/:partnerId/revoke-tokens',

  AdminFetchWebhookSubscriptions = '/v1/internal/webhooks/subscriptions/:partnerId',
  AdminCreateWebhookSubscription = '/v1/internal/webhooks/subscriptions',
  AdminUpdateWebhookSubscription = '/v1/internal/webhooks/subscriptions/:subscriptionId',
  AdminFetchWebhookDeliveries = '/v1/internal/webhooks/deliveries',
  AdminSendWebhook = '/v1/internal/webhooks/send',

  AdminAiDocumentReviewConfigs = '/v1/internal/ai-document-review-configs',
  AdminAiDocumentReviewConfig = '/v1/internal/ai-document-review-configs/:aiDocumentReviewConfigId',

  // Internal Document Review — contractor queue. ENG-489.
  InternalDocumentReviewSteps = '/v1/internal/document-review/contractor/steps',
  InternalDocumentReviewStep = '/v1/internal/document-review/contractor/steps/:collectDataStepId',
  InternalDocumentReviewRejectionReasons = '/v1/internal/document-review/contractor/rejection-reasons',
  InternalDocumentReviewApproveStep = '/v1/internal/document-review/contractor/approve-step',
  InternalDocumentReviewRecollectStep = '/v1/internal/document-review/contractor/recollect-step',

  // Internal Document Review — master-contractor queue. ENG-489 / ENG-490.
  InternalMcDocumentReviewDocuments = '/v1/internal/document-review/master-contractor/documents',
  InternalMcDocumentReviewApproveDocument = '/v1/internal/document-review/master-contractor/approve-document',
  InternalMcDocumentReviewRejectDocument = '/v1/internal/document-review/master-contractor/reject-document',
  // Payment retry schedulers (ENG-494). The Notion spec documents the pre-migration
  // `/v1/gigsafe/admin/schedulers/...` routes — these are the internal ones the app calls.
  // `/templates` is declared before `/:configId` so the literal path reads as the more specific
  // route, matching InternalRetryConfigsController.
  AdminFetchRetryConfigs = '/v1/internal/payments/schedulers/retry-configs',
  AdminFetchRetryConfigTemplates = '/v1/internal/payments/schedulers/retry-configs/templates',
  AdminUpdateRetryConfigTemplate = '/v1/internal/payments/schedulers/retry-configs/templates/:retryActionType',
  AdminFetchRetryConfig = '/v1/internal/payments/schedulers/retry-configs/:configId',
  AdminUpdateRetryConfig = '/v1/internal/payments/schedulers/retry-configs/:configId',
  AdminRescheduleRetryJob = '/v1/internal/payments/schedulers/retry-configs/:configId/retry-jobs/:retryJobId',

  // Update Partner Driver (ENG-486 / ENG-487). InternalContractorsController. The Notion page's
  // `/gigsafe/admin/dashboard/…` and `/ops-tools/…` paths are the pre-migration routes.
  InternalContractorsSearch = '/v1/internal/contractors/search',
  InternalContractor = '/v1/internal/contractors/:partnerDriverId',
  InternalContractorSteps = '/v1/internal/contractors/:partnerDriverId/steps',
  InternalContractorTemplate = '/v1/internal/contractors/:partnerDriverId/collect-data-template',
  InternalContractorOnboardingStatus = '/v1/internal/contractors/:partnerDriverId/switch-onboarding-status',
  InternalContractorStepReset = '/v1/internal/contractors/steps/:stepId/reset',
  InternalContractorStepCompleteFailed = '/v1/internal/contractors/steps/:stepId/complete-failed-step',
  InternalContractorStepCompleteBackgroundCheck = '/v1/internal/contractors/steps/:stepId/complete-background-check',
  InternalContractorStepCompleteVehicleInsurance = '/v1/internal/contractors/steps/:stepId/complete-vehicle-and-insurance',
  InternalContractorStepCompleteDriverLicense = '/v1/internal/contractors/steps/:stepId/complete-driver-license',
  InternalContractorStepCompleteLmsTraining = '/v1/internal/contractors/steps/:stepId/complete-lms-training',
  InternalContractorStepCompleteInsuranceDeclaration = '/v1/internal/contractors/steps/:stepId/complete-insurance-amount-declaration',
  InternalContractorStepCompleteVehicleRegistration = '/v1/internal/contractors/steps/:stepId/complete-vehicle-registration',
  InternalContractorStepCompleteDotNumber = '/v1/internal/contractors/steps/:stepId/complete-dot-number',
  // Optional steps only; the backend refuses a required one.
  InternalContractorStepSkip = '/v1/internal/contractors/steps/:stepId/skip',
  // `contractor_data_delete`. Body `{ reason }` only — the DTO forbids unknown keys.
  InternalContractorDeleteData = '/v1/internal/contractors/:partnerDriverId/delete-data',
  // POST uploads, DELETE removes every document on the step (no per-file delete exists).
  InternalContractorStepDocuments = '/v1/internal/contractors/steps/:stepId/document',

  // ── Payments Ops: real internal routes ───────────────────────────────────────────────────
  // One path serves GET (list) and POST (create); two members so each call site names its intent.
  AdminFetchFeatureFlags = '/v1/internal/feature-flags',
  AdminCreateFeatureFlag = '/v1/internal/feature-flags',
  AdminUpdateFeatureFlag = '/v1/internal/feature-flags/:flagId',
  AdminFetchCapturedEvents = '/v1/internal/payments/us-bank-killswitch/captured-events',
  // Literal sub-paths before `:eventId`, mirroring the controller's own ordering.
  AdminBulkReplayCapturedEvents = '/v1/internal/payments/us-bank-killswitch/captured-events/bulk-replay',
  AdminReplayAllCapturedEvents = '/v1/internal/payments/us-bank-killswitch/captured-events/replay-all',
  AdminReplayCapturedEvent = '/v1/internal/payments/us-bank-killswitch/captured-events/:eventId/replay',
  AdminDiscardCapturedEvent = '/v1/internal/payments/us-bank-killswitch/captured-events/:eventId/discard',

  // ── Payments Ops: internal routes from para-node PR #6894 (ENG-1129) ─────────────────────
  // These replaced the legacy `/v1/gigsafe/admin/payments/*` surface, which rejects WorkOS tokens.
  AdminFetchPendingPayments = '/v1/internal/payments/earnings-review',
  AdminFetchPendingPaymentDetails = '/v1/internal/payments/earnings-review/:earningId',
  AdminReviewPayment = '/v1/internal/payments/earnings-review/:earningId/decision',
  AdminFetchMissingInvoiceTransactions = '/v1/internal/payments/transactions/missing-invoice',
  AdminFetchPendingReviewTransactions = '/v1/internal/payments/transactions/pending-authorization',
  AdminFetchPartnerPendingInvoices = '/v1/internal/partners/:partnerId/invoices/pending',
  AdminAssignTransactionInvoice = '/v1/internal/payments/transactions/:transactionId/assign-invoice',
  // POST `{ transactionId }` — the refund is initiated from the refunds collection, not from the
  // transaction. Shares a path with the refunds GET; two members so each call site names its intent.
  AdminRefundTransaction = '/v1/internal/payments/refunds',
  AdminAuthorizeTransaction = '/v1/internal/payments/transactions/:transactionId/authorize',
  AdminFetchRefunds = '/v1/internal/payments/refunds',
  AdminExecuteRefund = '/v1/internal/payments/refunds/:refundId/execute',
  // GET lists, POST starts; same path, two members.
  AdminFetchBankOnboardings = '/v1/internal/payments/bank-onboardings',
  AdminStartBankOnboarding = '/v1/internal/payments/bank-onboardings',
  AdminFetchUsBankEarnings = '/v1/internal/payments/us-bank-transactions/earnings',
  AdminFetchUsBankEarningOrders = '/v1/internal/payments/us-bank-transactions/earnings/:earningId/orders',
  AdminFetchWallets = '/v1/internal/payments/wallets',
  AdminRunReconciliation = '/v1/internal/payments/reconciliation',
  AdminFetchFeeWithdrawals = '/v1/internal/payments/fee-withdrawals',
  // Streams the PDF (`application/pdf`); 404 when no report is stored. ENG-1212.
  AdminFetchFeeWithdrawalReport = '/v1/internal/payments/fee-withdrawals/:feeWithdrawalId/report',
}
